import { resolvePeriod, type AnalyticsFilterState } from './useAnalyticsFilters'

/* ─── Query-string construction for the MCP Analytics endpoints ───

   One builder, so the four endpoints cannot disagree about how a filter is
   spelled. Three contract details are easy to get wrong and are pinned here:

   * `channels` is exactly `agent` | `external`.
   * `users` carries **email**, not sub — the service resolves email→sub before any
     roll-up query, because email must never enter a roll-up grain.
   * Multi-valued filters repeat the parameter (`?channels=agent&channels=external`)
     rather than sending a comma-joined string, which FastAPI would read as one
     value and reject. */

export const MCP_ANALYTICS_BASE = '/api/v1/mcp-analytics'

function appendAll(params: URLSearchParams, key: string, values: readonly string[]) {
  for (const value of values) params.append(key, value)
}

export interface PeriodOverride {
  from: string
  to: string
}

/** The shared filter bar's values. `period` overrides the resolved window, which
 *  the chart uses at day granularity and a bar click uses for one bucket. */
export function buildFilterParams(
  filters: AnalyticsFilterState,
  period?: PeriodOverride,
): URLSearchParams {
  const params = new URLSearchParams()
  const { from, to } = period ?? resolvePeriod(filters)
  params.set('from', from)
  params.set('to', to)
  appendAll(params, 'users', filters.users)
  appendAll(params, 'channels', filters.channels)
  appendAll(params, 'invocationModes', filters.invocationModes)
  return params
}

export interface TableParams {
  sortCol: string
  sortDir: string
  page: number
  pageSize: number
}

export function withTableParams(params: URLSearchParams, table: TableParams): URLSearchParams {
  params.set('sortCol', table.sortCol)
  params.set('sortDir', table.sortDir)
  params.set('page', String(table.page))
  params.set('pageSize', String(table.pageSize))
  return params
}

export function withInvocationTypes(
  params: URLSearchParams,
  invocationTypes: readonly string[],
): URLSearchParams {
  appendAll(params, 'invocationTypes', invocationTypes)
  return params
}
